// Export-only passes (§6.2 pass 19): halving pyramid, separable Lanczos-3, output sharpening fused with
// the output transform. All resampling is done on PREMULTIPLIED linear values (§6.2 alpha rule).
// DEVIATION D13: resizing happens in linear light before the output encode (the plan lists the output
// transform first); resizing encoded values darkens fine detail. Sharpening stays on encoded luma.
import { COLOR_FNS, HEADER, OKLAB_FNS } from './common';
import { GRAIN_FNS } from './effectsPasses';

/** 2×2 box average (one pyramid level). Input straight (uInPremul = 0) or premultiplied; output premultiplied. */
export const HALVE = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uSrcSize;
uniform int uInPremul;
out vec4 o;
vec4 at(vec2 p) {
  vec4 c = texture(uSrc, (min(p, uSrcSize - 0.5)) / uSrcSize);
  if (uInPremul == 0) c.rgb *= c.a;
  return c;
}
void main() {
  vec2 b = floor(gl_FragCoord.xy) * 2.0;
  o = 0.25 * (at(b + vec2(0.5, 0.5)) + at(b + vec2(1.5, 0.5)) + at(b + vec2(0.5, 1.5)) + at(b + vec2(1.5, 1.5)));
}`;

/** Converts straight → premultiplied (used when no pyramid level is needed). */
export const PREMUL = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uSrcSize;
out vec4 o;
void main() { vec4 c = texture(uSrc, gl_FragCoord.xy / uSrcSize); o = vec4(c.rgb * c.a, c.a); }`;

/**
 * One separable Lanczos-3 pass on premultiplied input. For destination pixel d (along uDir), the source
 * centre is c = d * uScale + uOffset (pixel-centre coordinates, i.e. +0.5 conventions folded in by the
 * caller). uScale > 1 is a downscale: the kernel widens to 3·uScale taps each side (anti-aliasing).
 */
export const LANCZOS = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uSrcSize;
uniform vec2 uDir;          // (1,0) horizontal, (0,1) vertical
uniform float uScale;       // source pixels per destination pixel along uDir
uniform float uOffset;      // source coordinate of destination pixel 0's centre
uniform int uFinal;         // 1 = unpremultiply the result
out vec4 o;
const float PI = 3.141592653589793;
float sinc(float x) { return abs(x) < 1e-5 ? 1.0 : sin(PI * x) / (PI * x); }
float lanczos3(float x) { return abs(x) < 3.0 ? sinc(x) * sinc(x / 3.0) : 0.0; }
void main() {
  float d = dot(gl_FragCoord.xy - 0.5, uDir);
  float c = d * uScale + uOffset;               // source pixel-centre coordinate (0 = first pixel's centre)
  float k = max(1.0, uScale);
  float support = 3.0 * k;
  float first = ceil(c - support);
  float last = floor(c + support);
  vec2 other = gl_FragCoord.xy * (vec2(1.0) - uDir);
  vec4 acc = vec4(0.0);
  float wsum = 0.0;
  for (int i = 0; i < 64; i++) {
    float s = first + float(i);
    if (s > last) break;
    float w = lanczos3((s - c) / k);
    float sc = clamp(s, 0.0, dot(uSrcSize, uDir) - 1.0);
    vec2 p = uDir * (sc + 0.5) + other;
    acc += w * texture(uSrc, p / uSrcSize);
    wsum += w;
  }
  vec4 r = acc / wsum;
  r.a = clamp(r.a, 0.0, 1.0);
  if (uFinal == 1) r = vec4(r.a > 1e-6 ? max(r.rgb, vec3(0.0)) / r.a : vec3(0.0), r.a);
  o = r;
}`;

/**
 * Output transform + output sharpening (§6.2 passes 18–19) for an export tile. Reads the linear
 * straight-alpha tile (with an apron), encodes to the output space, applies an unsharp mask on encoded
 * luma (radius uRadius px, amount uAmount), composites the fill if flattening, and writes the inner
 * rect (uInner) only — the caller sizes the target to the inner rect.
 */
export const OUTPUT_SHARPEN = `${HEADER}${COLOR_FNS}${OKLAB_FNS}${GRAIN_FNS}
uniform sampler2D uSrc;
uniform vec2 uSrcSize;
uniform vec2 uInnerOrigin;  // inner rect origin inside the source tile
uniform mat3 uToOut;
uniform int uAdobe;
uniform int uFlatten;
uniform vec3 uFill;
uniform float uAmount;      // 0 = no sharpening
uniform float uRadius;
out vec4 o;
float enc(float l) { return uAdobe == 1 ? pow(max(l, 0.0), 1.0 / 2.19921875) : linToSrgb(max(l, 0.0)); }
vec3 encodeAt(vec2 p) {
  vec3 lin = clamp(gamutOut(texture(uSrc, p / uSrcSize).rgb, uToOut), 0.0, 1.0);
  return vec3(enc(lin.r), enc(lin.g), enc(lin.b));
}
float lumaE(vec3 e) { return dot(e, vec3(0.2126, 0.7152, 0.0722)); }
void main() {
  vec2 p = gl_FragCoord.xy + uInnerOrigin;
  vec4 c = texture(uSrc, p / uSrcSize);
  vec3 e = encodeAt(p);
  if (uAmount > 0.0 && c.a > 0.5) {
    // Gaussian blur of encoded luma, 5×5 taps at sigma = uRadius, weighted by each tap's alpha: transparent
    // pixels (rgb 0) must not pull the blur down, or the subject's edge is lifted into a light rim
    // (CODE_REVIEW S5). Fades in with alpha so the soft edge itself is left alone.
    float acc = 0.0, wsum = 0.0;
    for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
      vec2 q = p + vec2(float(i), float(j));
      float w = exp(-0.5 * float(i * i + j * j) / (uRadius * uRadius)) * clamp(texture(uSrc, q / uSrcSize).a, 0.0, 1.0);
      acc += w * lumaE(encodeAt(q));
      wsum += w;
    }
    float l = lumaE(e);
    float sharpened = l + uAmount * smoothstep(0.5, 0.95, c.a) * (l - acc / max(wsum, 1e-6));
    e = clamp(e + (sharpened - l), 0.0, 1.0);
  }
  e = addGrain(e, gl_FragCoord.xy);
  float a = clamp(c.a, 0.0, 1.0);
  if (uFlatten == 1) { e = mix(uFill, e, a); a = 1.0; }
  o = vec4(e, a);
}`;
